import React, { Fragment, useEffect, useRef, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Configure, InstantSearch, useHits, useSearchBox } from "react-instantsearch";
import { formatHighlightedField, useAlgoliaClient, useSearchInput } from "./utils";

const SearchInput = ({ onFocus, inputRef, autoFocus = false, onSubmitQuery }) => {
  const { value, onChange, onReset, onSubmit } = useSearchInput({
    onSubmit: (nextValue) => {
      const next = (nextValue || "").trim();
      if (!next) return;
      if (typeof onSubmitQuery === "function") {
        onSubmitQuery(next);
      }
    },
  });

  return (
    <div className="c-header-search-popover__input">
      <span className="c-header-search-popover__icon" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M12.5799 13.7736L7.82362 9.01706C7.45162 9.30519 7.02262 9.53375 6.53662 9.70275C6.05062 9.87175 5.53113 9.95625 4.97813 9.95625C3.58863 9.95625 2.41156 9.47394 1.44694 8.50931C0.482313 7.54456 0 6.3675 0 4.97813C0 3.58875 0.482313 2.41169 1.44694 1.44694C2.41156 0.482312 3.58863 0 4.97813 0C6.3675 0 7.5445 0.482312 8.50912 1.44694C9.47388 2.41169 9.95625 3.58875 9.95625 4.97813C9.95625 5.53413 9.87175 6.05363 9.70275 6.53662C9.53362 7.01962 9.30506 7.44563 9.01706 7.81463L13.7779 12.5799L12.5799 13.7736ZM4.97813 8.25C5.88863 8.25 6.66156 7.93231 7.29694 7.29694C7.93231 6.66169 8.25 5.88875 8.25 4.97813C8.25 4.0675 7.93231 3.29456 7.29694 2.65931C6.66156 2.02394 5.88863 1.70625 4.97813 1.70625C4.0675 1.70625 3.2945 2.02394 2.65913 2.65931C2.02388 3.29456 1.70625 4.0675 1.70625 4.97813C1.70625 5.88875 2.02388 6.66169 2.65913 7.29694C3.2945 7.93231 4.0675 8.25 4.97813 8.25Z"
            fill="#DB2486"
          />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        autoFocus={autoFocus}
        placeholder="Ex. waterproofing, basement, etc…"
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        aria-label="Search site"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
      {value ? (
        <button
          type="button"
          className="c-header-search-popover__clear"
          onClick={onReset}
          aria-label="Clear search"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="9"
            height="9"
            viewBox="0 0 9 9"
            fill="none"
          >
            <line
              x1="0.843826"
              y1="0.706956"
              x2="8.27855"
              y2="8.14168"
              stroke="#737373"
              strokeWidth="2"
            />
            <line
              y1="-1"
              x2="10.5143"
              y2="-1"
              transform="matrix(-0.707107 0.707107 0.707107 0.707107 8.84961 1.41406)"
              stroke="#737373"
              strokeWidth="2"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div className="c-header-search-popover__section-label">
    <span>{children}</span>
  </div>
);

const ArrowIcon = () => (
  <span className="c-header-search-popover__hit-icon" aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
    >
      <circle cx="8.5" cy="8.5" r="8" stroke="#A7A8AA" />
      <line x1="5.39062" y1="8.1543" x2="11.7232" y2="8.1543" stroke="#A7A8AA" />
      <path d="M8.70636 4.33398L12.0234 8.00028L8.70636 11.6666" stroke="#A7A8AA" />
    </svg>
  </span>
);

const ServiceHit = ({ hit }) => (
  <a className="c-header-search-popover__hit c-header-search-popover__hit--service" href={hit.url}>
    {hit.imageUrl ? (
      <span className="c-header-search-popover__thumb">
        <img src={hit.imageUrl} alt={hit.imageAltText || hit.headline || ""} />
      </span>
    ) : (
      <span className="c-header-search-popover__thumb c-header-search-popover__thumb--empty" aria-hidden="true" />
    )}
    <div className="c-header-search-popover__hit-body">
      <p
        className="c-header-search-popover__hit-title"
        dangerouslySetInnerHTML={{ __html: formatHighlightedField(hit) }}
      />
    </div>
  </a>
);

const BlogHit = ({ hit }) => (
  <a className="c-header-search-popover__hit c-header-search-popover__hit--blog" href={hit.url}>
    <ArrowIcon />
    <p
      className="c-header-search-popover__hit-title"
      dangerouslySetInnerHTML={{ __html: formatHighlightedField(hit) }}
    />
  </a>
);

const PressHit = ({ hit }) => (
  <a className="c-header-search-popover__hit c-header-search-popover__hit--press" href={hit.url}>
    <ArrowIcon />
    <p
      className="c-header-search-popover__hit-title"
      dangerouslySetInnerHTML={{ __html: formatHighlightedField(hit) }}
    />
  </a>
);

const DifferenceHit = ({ hit }) => (
  <a className="c-header-search-popover__hit c-header-search-popover__hit--difference" href={hit.url}>
    <ArrowIcon />
    <p
      className="c-header-search-popover__hit-title"
      dangerouslySetInnerHTML={{ __html: formatHighlightedField(hit) }}
    />
  </a>
);

const HitsSection = ({ title, hits, hitComponent: HitComponent }) => {
  if (!hits || hits.length === 0) return null;

  return (
    <div className="c-header-search-popover__section">
      <SectionLabel>{title}</SectionLabel>
      <div className="c-header-search-popover__hits">
        {hits.map((hit) => (
          <HitComponent key={hit.objectID || hit.id} hit={hit} />
        ))}
      </div>
    </div>
  );
};

const HeaderSearchPopover = ({
  appId: propAppId,
  searchKey: propSearchKey,
  indexName = "global",
}) => {
  const { client, appId, searchKey } = useAlgoliaClient(propAppId, propSearchKey);
  const inputRef = useRef(null);
  const searchResultsPath = "/q";
  const [isLg, setIsLg] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsLg(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  if (!client || !appId || !searchKey) return null;
  if (!isLg) return null;

  const focusInput = () => {
    if (typeof window === "undefined") return;
    window.requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (typeof inputRef.current.select === "function") {
          inputRef.current.select();
        }
      }
    });
  };

  const Results = ({ children }) => {
    const { hits, results, status } = useHits();
    const { query } = useSearchBox();
    const hasQuery = typeof query === "string" && query.trim().length > 0;
    const hasHits = Array.isArray(hits) && hits.length > 0;
    return (
      <>
        {children({ hits, status, hasHits, hasQuery })}
        {hasQuery && !hasHits && status !== "loading" ? (
          <div className="c-header-search-popover__empty">No results found.</div>
        ) : null}
        {hasQuery && results?.nbHits === 0 && status === "stalled" ? (
          <div className="c-header-search-popover__empty">No results found.</div>
        ) : null}
      </>
    );
  };

  return (
    <Popover className="c-header-search-popover">
      {({ open, close }) => (
        <>
          <Popover.Button
            className="g-header__secondary-search-trigger"
            onClick={focusInput}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                focusInput();
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="21"
              viewBox="0 0 23 21"
              fill="none"
            >
              <circle cx="8.5" cy="8.5" r="7.5" stroke="currentColor" strokeWidth="2" />
              <path stroke="currentColor" strokeWidth="2" d="m14.707 13.293 7 7" />
            </svg>
            <span>Search…</span>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-smooth duration-500"
            enterFrom="opacity-0 translate-x-2"
            enterTo="opacity-100 translate-x-0"
            leave="transition ease-smooth duration-500"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 translate-x-2"
          >
            <Popover.Panel className="c-header-search-popover__panel">
              <InstantSearch
                searchClient={client}
                indexName={indexName}
                future={{ preserveSharedStateOnUnmount: true }}
              >
                <Configure hitsPerPage={20} />
                <Results>
                  {({ hits, hasHits, hasQuery }) => {
                    const services = hits
                      ?.filter((hit) => hit.entryType === "servicesPages")
                      .slice(0, 4);
                    const blog = hits
                      ?.filter((hit) => hit.entryType === "blog")
                      .slice(0, 5);
                    const press = hits
                      ?.filter((hit) => hit.entryType === "press")
                      .slice(0, 4);
                    const difference = hits
                      ?.filter(
                        (hit) =>
                          hit.entryType === "ourDifference" ||
                          hit.entryType === "ourDifferencePages"
                    )
                    .slice(0, 4);

                      return (
                        <div className="c-header-search-popover__panel-inner">
                          <div className="c-header-search-popover__panel-input">
                            <SearchInput
                              onFocus={() => {}}
                              inputRef={inputRef}
                              autoFocus
                              onSubmitQuery={(term) => {
                                const url = `${searchResultsPath}?query=${encodeURIComponent(term)}`;
                                window.location.assign(url);
                              }}
                            />
                          </div>
                          <div
                            className={`c-header-search-popover__results${
                              !hasQuery || !hasHits ? " is-hidden" : ""
                            }`}
                          >
                          <div className="c-header-search-popover__sections">
                            <HitsSection
                              title="In Services"
                              hits={services}
                              hitComponent={ServiceHit}
                            />
                            <HitsSection
                              title="In Blog"
                              hits={blog}
                              hitComponent={BlogHit}
                            />
                            <HitsSection
                              title="In Press"
                              hits={press}
                              hitComponent={PressHit}
                            />
                            <HitsSection
                              title="In Our Difference"
                              hits={difference}
                              hitComponent={DifferenceHit}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </Results>
              </InstantSearch>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default HeaderSearchPopover;
